import React, { useState, useEffect } from "react";
import { Modal, Tag, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { BreathAPIs } from "../../APIs/BreathAPIs";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./styles.module.css";

// --- Components for Drag and Drop ---

const SortableRow = ({ row, id, fmt, pct, styles }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "#e6f7ff" : row.isCustom ? "#fafafa" : "white",
    zIndex: isDragging ? 1 : "auto",
    cursor: "move", // Indicates the whole row is draggable
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <td>
        <b>{row.title}</b>
        {row.isCustom && <Tag style={{ marginLeft: 6 }} color="blue">Custom</Tag>}
        {row.description && (
          <div style={{ fontSize: "11px", color: "gray" }}>{row.description}</div>
        )}
      </td>
      <td style={{ textAlign: "right" }}>{row.qty}</td>
      <td style={{ textAlign: "right" }}>{fmt(row.costPerUnit)}</td>
      <td style={{ textAlign: "right" }}>{fmt(row.totalCost)}</td>
      <td style={{ textAlign: "right" }}>{fmt(row.margin)}</td>
      <td style={{ textAlign: "right" }}>{fmt(row.baseTotal)}</td>
      <td style={{ textAlign: "right", color: "green" }}>{pct(row.marginPct)}</td>
      <td style={{ textAlign: "right" }}>{fmt(row.pricePaidPerUnit)}</td>
      <td style={{ textAlign: "right" }}>
        {row.salesTaxApplicable ? pct(row.salesTaxPercent) : <span style={{ color: "gray", fontSize: "11px" }}>N/A</span>}
      </td>
      <td style={{ textAlign: "right", color: row.taxAmt > 0 ? "#d46b08" : "gray" }}>
        {row.taxAmt > 0 ? fmt(row.taxAmt) : "—"}
      </td>
      
      <td style={{ textAlign: "right", fontWeight: "bold" }}>{fmt(row.TotalAmount)}</td>
    </tr>
  );
};

// --- Main Modal Component ---

const BidDetailModal = ({ bid, open, onClose }) => {
  const [items, setItems] = useState([]);
  console.log("bid data: ", bid)

  // Sensors for DND
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (bid) {
      const rows = prepareRows(bid);
      // Sort by current orderNumber before setting state
      rows.sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
      setItems(rows);
    }
  }, [bid]);

  if (!bid) return null;

  function prepareRows(bidData) {
    const standardKeys = [
      { key: "l2Charger", label: "L2 Charger" },
      { key: "l3Charger", label: "L3 Charger" },
      { key: "electricalWork", label: "Electrical Work" },
      { key: "wifiAntenna", label: "Wifi Antenna" },
      { key: "wifiConnectivity", label: "Wifi Connectivity" },
      { key: "warranty", label: "Warranty" },
      { key: "signage", label: "Signage" },
      { key: "pedestals", label: "Pedestals" },
      { key: "shipping", label: "Shipping" },
      { key: "simCard4G", label: "simCard4G" },
      { key: "siteWiFi", label: "siteWiFi" },
      { key: "chargerInstallOnly", label: "chargerInstallOnly" },
    ];

    const rows = [];
    // Process Standard
    standardKeys.forEach(({ key, label }, index) => {
      const item = bidData[key];
      if (!item?.quantity) return;
      const baseTotal = item.quantity * item.pricePerUnit + (item.margin || 0);
      rows.push({
        id: key, // Unique ID for DND
        originalKey: key,
        orderNumber: item.orderNumber ?? index,
        title: item.title || label,
        description: item.description,
        qty: item.quantity,
        costPerUnit: item.pricePerUnit,
        pricePaidPerUnit: (baseTotal)/(item.quantity || 1),
        totalCost: item.quantity * item.pricePerUnit,
        margin: item.margin || 0,
        marginPct: baseTotal > 0 ? ((item.margin || 0) / baseTotal) * 100 : 0,
        salesTaxApplicable: item.salesTaxApplicable,
        salesTaxPercent: item.salesTaxPercent || 0,
        taxAmt: item.salesTaxApplicable ? (baseTotal * (item.salesTaxPercent || 0)) / 100 : 0,
        baseTotal,
        TotalAmount: baseTotal + (item.salesTaxApplicable ? (baseTotal * (item.salesTaxPercent || 0)) / 100 : 0),
      });
    });

    // Process Custom
    (bidData.bidCustomData || []).forEach((item, index) => {
      if (!item?.fieldQuantity) return;
      const baseTotal = item.fieldQuantity * item.fieldPricePerUnit + (item.fieldMargin || 0);
      rows.push({
        id: `custom-${index}`, 
        isCustom: true,
        customIndex: index,
        orderNumber: item.orderNumber ?? (100 + index),
        title: item.fieldTitle || "Custom Item",
        description: item.fieldDescription,
        qty: item.fieldQuantity,
        costPerUnit: item.fieldPricePerUnit,
        pricePaidPerUnit: (baseTotal)/(item.fieldQuantity || 1),
        totalCost: item.fieldQuantity * item.fieldPricePerUnit,
        margin: item.fieldMargin || 0,
        marginPct: baseTotal > 0 ? ((item.fieldMargin || 0) / baseTotal) * 100 : 0,
        salesTaxApplicable: item.fieldsalesTaxApplicable,
        salesTaxPercent: item.fieldsalesTaxPercent || 0,
        taxAmt: item.fieldsalesTaxApplicable ? (baseTotal * (item.fieldsalesTaxPercent || 0)) / 100 : 0,
        baseTotal,
        TotalAmount: baseTotal + (item.fieldsalesTaxApplicable ? (baseTotal * (item.fieldsalesTaxPercent || 0)) / 100 : 0),
      });
    });

    return rows;
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      
      const newOrder = arrayMove(items, oldIndex, newIndex);
      
      // Update local state immediately for UI smoothness
      setItems(newOrder);

      // Prepare payload for Backend
      // We map through the new array and assign orderNumbers based on index
      const updatedBidData = { ...bid };
      
      newOrder.forEach((item, idx) => {
        if (item.isCustom) {
          updatedBidData.bidCustomData[item.customIndex].orderNumber = idx;
        } else {
          updatedBidData[item.originalKey].orderNumber = idx;
        }
      });


      // if (onUpdateBid) {
        try {
           await BreathAPIs.updateBid(updatedBidData, updatedBidData?._id)
          message.success("Order updated");
        } catch (err) {
          message.error("Failed to save order");
        }
      // }
    }
  };

  const csvEscape = (value) => {
    const str = value === null || value === undefined ? "" : String(value);
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const handleDownloadCSV = () => {
    const headers = [
      "Item",
      "Description",
      "Qty",
      "Cost/Unit",
      "Total Cost",
      "Margin ($)",
      "Base Total",
      "Margin %",
      "Price Paid/Unit",
      "Tax %",
      "Tax Amt",
      "Total Amount",
    ];

    const rows = items.map((row) => [
      row.title,
      row.description || "",
      row.qty,
      row.costPerUnit,
      row.totalCost,
      row.margin,
      row.baseTotal,
      row.marginPct?.toFixed(2),
      row.pricePaidPerUnit,
      row.salesTaxApplicable ? row.salesTaxPercent : "N/A",
      row.taxAmt || 0,
      row.TotalAmount,
    ]);

    const csvLines = [headers, ...rows].map((r) => r.map(csvEscape).join(","));

    csvLines.push("");
    csvLines.push(["Subtotal", bid.subTotal].map(csvEscape).join(","));
    csvLines.push([
      bid.salesTaxPercent ? `Sales Tax (${Number(bid.salesTaxPercent).toFixed(2)}%)` : "Sales Tax",
      bid.salesTax,
    ].map(csvEscape).join(","));
    if (bid.rebateTitle) {
      csvLines.push([bid.rebateTitle, `-${bid.rebateAmount}`].map(csvEscape).join(","));
    }
    csvLines.push(["Total Cost", bid.totalCost].map(csvEscape).join(","));
    csvLines.push(["Total Margin", bid.totalMargin].map(csvEscape).join(","));

    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", `Bid-${bid.bidNumber || bid._id}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered width="90%" title={<span>Bid Detail — <b>{bid.bidNumber}</b> <Tag color="green">{bid.bidStatus}</Tag></span>}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
        <Button icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
          Download CSV
        </Button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table border="1" cellPadding="6" className={styles["table"]} style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f0f0f0" }}>
                <th style={{ textAlign: "left" }}>Item </th>
                <th style={{ textAlign: "right" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Cost/Unit</th>
                <th style={{ textAlign: "right" }}>Total Cost</th>
                <th style={{ textAlign: "right" }}>Margin ($)</th>
                <th style={{ textAlign: "right" }}>Base Total</th>
                <th style={{ textAlign: "right" }}>Margin %</th>
                <th style={{ textAlign: "right" }}>Price Paid/Unit</th>
                <th style={{ textAlign: "right" }}>Tax %</th>
                <th style={{ textAlign: "right" }}>Tax Amt</th>
                
                <th style={{ textAlign: "right" }}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {items.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: "center" }}>No line items</td></tr>
                ) : (
                  items.map((row) => (
                    <SortableRow key={row.id} id={row.id} row={row} fmt={fmt} pct={pct} styles={styles} />
                  ))
                )}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>
      {/* Totals */}
       <div
         style={{
           marginTop: "20px",
           display: "flex",
          flexDirection: "column",
           alignItems: "flex-end",
           gap: "6px",
           fontSize: "14px",
         }}
       >
        <div style={{ display: "flex", gap: "40px" }}>
           <span style={{ color: "gray" }}>Subtotal</span>
           <b style={{ minWidth: "120px", textAlign: "right" }}>
             {fmt(bid.subTotal)}
           </b>
         </div>
         <div style={{ display: "flex", gap: "40px" }}>
           <span style={{ color: "gray" }}>
             Sales Tax{bid.salesTaxPercent ? ` (${Number(bid.salesTaxPercent).toFixed(2)}%)` : ""}
           </span>
         <b style={{ minWidth: "120px", textAlign: "right", color: "#d46b08" }}>
             {fmt(bid.salesTax)}
           </b>
         </div>
         {bid.rebateTitle && (
           <div style={{ display: "flex", gap: "40px" }}>
             <span style={{ color: "gray" }}>{bid.rebateTitle}</span>
             <b style={{ minWidth: "120px", textAlign: "right", color: "red" }}>
               - {fmt(bid.rebateAmount)}
             </b>
           </div>
         )}
         <div
           style={{
             display: "flex",
             gap: "40px",
             borderTop: "1px solid #d9d9d9",
             paddingTop: "6px",
             marginTop: "4px",
           }}
         >
           <span>
             <b>Total Cost</b>
           </span>
           <b style={{ minWidth: "120px", textAlign: "right", fontSize: "16px" }}>
             {fmt(bid.totalCost)}
           </b>
         </div>
         <div style={{ display: "flex", gap: "40px" }}>
           <span style={{ color: "gray" }}>Total Margin</span>
           <b style={{ minWidth: "120px", textAlign: "right", color: "green" }}>
             {fmt(bid.totalMargin)}
           </b>
         </div>
       </div>

       {/* Comments */}
       {bid.comments?.length > 0 && (
         <div style={{ marginTop: "20px" }}>
           <b>Comments:</b>
           {bid.comments.map((c, i) => (
             <p key={i} style={{ margin: "4px 0 0 0", fontSize: "13px" }}>
               {i + 1}. {c}
             </p>
           ))}
         </div>
       )}
    </Modal>
  );
};

const fmt = (num) => `$${Number(num || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (num) => `${Number(num || 0).toFixed(2)}%`;

export default BidDetailModal;







// import React from "react";
// import { Modal, Tag } from "antd";
// import styles from "./styles.module.css";

// const STATUS_COLORS = {
//   DRAFT: "default",
//   PENDINGAPPROVAL: "orange",
//   APPROVED: "green",
//   REJECTED: "red",
// };

// const fmt = (num) =>
//   num != null
//     ? `$${Number(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
//     : "$0.00";

// const pct = (num) =>
//   num != null ? `${Number(num).toFixed(2)}%` : "0.00%";

// const BidDetailModal = ({ bid, open, onClose }) => {
//   if (!bid) return null;

//   // Build rows from standard line items
//   const standardItems = [
//     { key: "l2Charger",        label: "L2 Charger" },
//     { key: "l3Charger",        label: "L3 Charger" },
//     { key: "electricalWork",   label: "Electrical Work" },
//     { key: "wifiAntenna",      label: "Wifi Antenna" },
//     { key: "wifiConnectivity", label: "Wifi Connectivity" },
//     { key: "warranty",         label: "Warranty" },
//     { key: "signage",          label: "Signage" },
//     { key: "pedestals",        label: "Pedestals" },
//     { key: "shipping",         label: "Shipping" },
//     { key: "simCard4G",        label: "simCard4G" },
//     { key: "siteWiFi",        label: "siteWiFi" },
//     { key: "chargerInstallOnly",        label: "chargerInstallOnly" },
//   ];

//   const itemRows = [];

//   standardItems.forEach(({ key, label }, index) => {  
//     const item = bid[key];
//     if (!item?.quantity) return;

//     const baseTotal = item.quantity * item.pricePerUnit + (item.margin || 0);
//     const taxAmt =
//       item.salesTaxApplicable && item.salesTaxPercent
//         ? (baseTotal * item.salesTaxPercent) / 100
//         : 0;
//     const marginPct =
//       baseTotal > 0 ? ((item.margin || 0) / baseTotal) * 100 : 0;

//     itemRows.push({
//       orderNumber: item.orderNumber ?? index,
//       title: item.title || label,
//       description: item.description,
//       qty: item.quantity,
//       costPerUnit: item.pricePerUnit,
//       margin: item.margin || 0,
//       marginPct,
//       salesTaxApplicable: item.salesTaxApplicable,
//       salesTaxPercent: item.salesTaxPercent || 0,
//       taxAmt,
//       baseTotal,
//       TotalAmount: baseTotal + taxAmt,
//     });
//   });

//   // Custom fields
//   (bid.bidCustomData || []).forEach((item, index) => {
//     if (!item?.fieldQuantity) return;
//     const baseTotal =
//       item.fieldQuantity * item.fieldPricePerUnit + (item.fieldMargin || 0);
//     const taxAmt =
//       item.fieldsalesTaxApplicable && item.fieldsalesTaxPercent
//         ? (baseTotal * item.fieldsalesTaxPercent) / 100
//         : 0;
//     const marginPct =
//       baseTotal > 0 ? ((item.fieldMargin || 0) / baseTotal) * 100 : 0;

//     itemRows.push({
//       orderNumber: item.orderNumber ?? (standardItems.length + index),
//       title: item.fieldTitle || "Custom Item",
//       description: item.fieldDescription,
//       qty: item.fieldQuantity,
//       costPerUnit: item.fieldPricePerUnit,
//       margin: item.fieldMargin || 0,
//       marginPct,
//       salesTaxApplicable: item.fieldsalesTaxApplicable,
//       salesTaxPercent: item.fieldsalesTaxPercent || 0,
//       taxAmt,
//       baseTotal,
//       isCustom: true,
//       TotalAmount: baseTotal + taxAmt,
//     });
//   });

//   itemRows.sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));

//   return (
//     <Modal
//       open={open}
//       onCancel={onClose}
//       footer={null}
//       centered
//       width="90%"
//       title={
//         <span>
//           Bid Detail —{" "}
//           <b>{bid.bidNumber}</b>{" "}
//           <Tag color={STATUS_COLORS[bid.bidStatus] || "default"}>
//             {bid.bidStatus}
//           </Tag>
//         </span>
//       }
//     >
//       {/* Header info */}
//       <div style={{ display: "flex", gap: "40px", marginBottom: "16px", flexWrap: "wrap" }}>
//         <div>
//           <div style={{ color: "gray", fontSize: "12px" }}>Client</div>
//           <b>{bid.dealId?.dealname || "—"}</b>
//         </div>
//         <div>
//           <div style={{ color: "gray", fontSize: "12px" }}>Deal</div>
//           <b>{bid.dealId?.installer_name || "—"}</b>
//         </div>
//         <div>
//           <div style={{ color: "gray", fontSize: "12px" }}>Created By</div>
//           <b>{bid.email || "—"}</b>
//         </div>
//         <div>
//           <div style={{ color: "gray", fontSize: "12px" }}>Date</div>
//           <b>{new Date(bid.dateCreated).toLocaleDateString()}</b>
//         </div>
//         <div>
//           <div style={{ color: "gray", fontSize: "12px" }}>Payment Type</div>
//           <b>{bid.paymentType || "—"}</b>
//         </div>
//       </div>

//       {/* Line items table */}
//       <div style={{ overflowX: "auto" }}>
//         <table
//           border="1"
//           cellPadding="6"
//           className={styles["table"]}
//           style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}
//         >
//           <thead>
//             <tr style={{ background: "#f0f0f0" }}>
//               <th style={{ textAlign: "left" }}>Item / Description</th>
//               <th style={{ textAlign: "right" }}>Qty</th>
//               <th style={{ textAlign: "right" }}>Cost/Unit</th>
//               <th style={{ textAlign: "right" }}>Base Total</th>
//               <th style={{ textAlign: "right" }}>Margin ($)</th>
//               <th style={{ textAlign: "right" }}>Margin %</th>
//               <th style={{ textAlign: "right" }}>Tax %</th>
//               <th style={{ textAlign: "right" }}>Tax Amt</th>
//               <th style={{ textAlign: "right" }}>Total Amount</th>
//             </tr>
//           </thead>
//           <tbody>
//             {itemRows.length === 0 ? (
//               <tr>
//                 <td colSpan="9" style={{ textAlign: "center", color: "gray" }}>
//                   No line items
//                 </td>
//               </tr>
//             ) : (
//               itemRows.map((row, i) => (
//                 <tr
//                   key={i}
//                   style={{ background: row.isCustom ? "#fafafa" : "white" }}
//                 >
//                   <td>
//                     <b>{row.title}</b>
//                     {row.isCustom && (
//                       <Tag style={{ marginLeft: 6 }} color="blue">Custom</Tag>
//                     )}
//                     {row.description && (
//                       <div style={{ fontSize: "11px", color: "gray" }}>
//                         {row.description}
//                       </div>
//                     )}
//                   </td>
//                   <td style={{ textAlign: "right" }}>{row.qty}</td>
//                   <td style={{ textAlign: "right" }}>{fmt(row.costPerUnit)}</td>
//                   <td style={{ textAlign: "right" }}>{fmt(row.baseTotal)}</td>
//                   <td style={{ textAlign: "right" }}>{fmt(row.margin)}</td>
//                   <td style={{ textAlign: "right", color: "green" }}>
//                     {pct(row.marginPct)}
//                   </td>
//                   <td style={{ textAlign: "right" }}>
//                     {row.salesTaxApplicable ? pct(row.salesTaxPercent) : (
//                       <span style={{ color: "gray", fontSize: "11px" }}>N/A</span>
//                     )}
//                   </td>
//                   <td style={{ textAlign: "right", color: row.taxAmt > 0 ? "#d46b08" : "gray" }}>
//                     {row.taxAmt > 0 ? fmt(row.taxAmt) : "—"}
//                   </td>
//                   <td style={{ textAlign: "right", fontWeight: "bold" }}>
//                     {fmt(row.TotalAmount)}
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Totals */}
//       <div
//         style={{
//           marginTop: "20px",
//           display: "flex",
//           flexDirection: "column",
//           alignItems: "flex-end",
//           gap: "6px",
//           fontSize: "14px",
//         }}
//       >
//         <div style={{ display: "flex", gap: "40px" }}>
//           <span style={{ color: "gray" }}>Subtotal</span>
//           <b style={{ minWidth: "120px", textAlign: "right" }}>
//             {fmt(bid.subTotal)}
//           </b>
//         </div>
//         <div style={{ display: "flex", gap: "40px" }}>
//           <span style={{ color: "gray" }}>
//             Sales Tax{bid.salesTaxPercent ? ` (${Number(bid.salesTaxPercent).toFixed(2)}%)` : ""}
//           </span>
//           <b style={{ minWidth: "120px", textAlign: "right", color: "#d46b08" }}>
//             {fmt(bid.salesTax)}
//           </b>
//         </div>
//         {bid.rebateTitle && (
//           <div style={{ display: "flex", gap: "40px" }}>
//             <span style={{ color: "gray" }}>{bid.rebateTitle}</span>
//             <b style={{ minWidth: "120px", textAlign: "right", color: "red" }}>
//               - {fmt(bid.rebateAmount)}
//             </b>
//           </div>
//         )}
//         <div
//           style={{
//             display: "flex",
//             gap: "40px",
//             borderTop: "1px solid #d9d9d9",
//             paddingTop: "6px",
//             marginTop: "4px",
//           }}
//         >
//           <span>
//             <b>Total Cost</b>
//           </span>
//           <b style={{ minWidth: "120px", textAlign: "right", fontSize: "16px" }}>
//             {fmt(bid.totalCost)}
//           </b>
//         </div>
//         <div style={{ display: "flex", gap: "40px" }}>
//           <span style={{ color: "gray" }}>Total Margin</span>
//           <b style={{ minWidth: "120px", textAlign: "right", color: "green" }}>
//             {fmt(bid.totalMargin)}
//           </b>
//         </div>
//       </div>

//       {/* Comments */}
//       {bid.comments?.length > 0 && (
//         <div style={{ marginTop: "20px" }}>
//           <b>Comments:</b>
//           {bid.comments.map((c, i) => (
//             <p key={i} style={{ margin: "4px 0 0 0", fontSize: "13px" }}>
//               {i + 1}. {c}
//             </p>
//           ))}
//         </div>
//       )}
//     </Modal>
//   );
// };

// export default BidDetailModal;
